import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTrackClickedPublicBoardLink } from "@air/analytics";
import { Button } from "@air/primitive-button";
import { Tooltip } from "@air/primitive-tooltip";
import Router from "next/router";
import { memo, useCallback, useEffect, useState } from "react";
import { usePublicWorkspace } from "~/components/PublicWorkspaceProvider";
import { createBoardRoute } from "~/constants/routes";
import { centralizedBoardSelector } from "~/store/centralizedBoard/selectors";
import { useWorkspaces } from "~/swr-hooks/workspaces/useWorkspaces";
import { useAirStore } from "~/utils/ReduxUtils";
// Public workspace header that matches private WorkspaceHeader structure
export const PublicWorkspaceHeader = /*#__PURE__*/ memo((param)=>{
    let { Search, headerActions } = param;
    const { workspaceName, workspaceImage } = usePublicWorkspace();
    const { data: workspaces } = useWorkspaces();
    const { workspaceId } = usePublicWorkspace();
    const store = useAirStore();
    const { trackClickedPublicBoardLink } = useTrackClickedPublicBoardLink();
    const [isInWorkspace, setIsInWorkspace] = useState(()=>{
        if (!workspaces || !workspaceId) return false;
        return workspaces.some((workspace)=>workspace.id === workspaceId);
    });
    useEffect(()=>{
        if (isInWorkspace || !workspaceId || !workspaces) return;
        setIsInWorkspace(workspaces.some((workspace)=>workspace.id === workspaceId));
    }, [
        workspaceId,
        isInWorkspace,
        workspaces
    ]);
    const onWorkspaceNavigate = useCallback(()=>{
        const centralizedBoard = centralizedBoardSelector(store.getState());
        if (!centralizedBoard) return;
        Router.push(createBoardRoute(centralizedBoard.id, centralizedBoard.title));
        trackClickedPublicBoardLink({
            name: "clicked view in workspace",
            page_area: "header",
            page_type: "public board"
        });
    }, [
        store,
        trackClickedPublicBoardLink
    ]);
    const getWorkspaceHeaderContent = useCallback(()=>{
        return /*#__PURE__*/ _jsxs("div", {
            className: "relative z-30 flex w-[var(--sidebar-width)] items-center gap-1 px-4",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "size-10 shrink-0 overflow-hidden rounded-8 border border-grey-5 bg-grey-5",
                    children: !!workspaceImage && /*#__PURE__*/ _jsx("img", {
                        alt: "Logo for ".concat(workspaceName),
                        className: "size-full object-cover",
                        src: workspaceImage,
                        width: 40,
                        height: 40
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex items-center gap-1",
                    children: /*#__PURE__*/ _jsx("span", {
                        className: "truncate text-16 font-semibold text-grey-11",
                        children: workspaceName
                    })
                })
            ]
        });
    }, [
        workspaceImage,
        workspaceName
    ]);
    const getWorkspaceHeader = useCallback(()=>{
        if (isInWorkspace) {
            return /*#__PURE__*/ _jsx(Tooltip, {
                label: "View in workspace",
                children: /*#__PURE__*/ _jsx(Button, {
                    className: "flex cursor-pointer items-center justify-start hover:bg-transparent",
                    appearance: "ghost",
                    onClick: onWorkspaceNavigate,
                    children: getWorkspaceHeaderContent()
                })
            });
        }
        return /*#__PURE__*/ _jsx("div", {
            className: "flex justify-start",
            children: getWorkspaceHeaderContent()
        });
    }, [
        getWorkspaceHeaderContent,
        isInWorkspace,
        onWorkspaceNavigate
    ]);
    return /*#__PURE__*/ _jsxs("header", {
        className: "grid h-18 shrink-0 items-center",
        style: {
            gridTemplateColumns: "var(--sidebar-width) 1fr auto"
        },
        children: [
            getWorkspaceHeader(),
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-center px-4",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "w-full max-w-[600px]",
                    children: Search
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end gap-2 px-4",
                children: headerActions
            })
        ]
    });
});
PublicWorkspaceHeader.displayName = "PublicWorkspaceHeader";
